import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useCompanyStore, usePatientStore, useAnswersStore } from '../../stores';
import { NavigationUtils, WindowDimensionsUtils } from '../../utils';
import { formatDate } from '../../utils/formatter';
import PatientResponsibleForm from './PatientResponsibleForm';

const footerPlusHeaderHeight = 344;

function PatientResponsibleContainer() {
	const { companySettings: { settings } } = useCompanyStore();
	const { patient } = usePatientStore();
	const { setAnswers, clearAnswers } = useAnswersStore();
	const dimensions = WindowDimensionsUtils.useWindowDimensions();
	const listMaxHeight = useMemo(() => (!dimensions?.height ? 300 : dimensions?.height - footerPlusHeaderHeight), [dimensions?.height]);
	const history = useHistory();

	const [showDependentForm, setShowDependentForm] = useState(false);
	const [dependentData, setDependentData] = useState({ born: '', gender: '' });

	const listAllPatients = useCallback(() => {
		const dependents = patient.dependents?.map((dependent) => ({
			...dependent,
			isMainPatient: false,
		})) || [];

		return [{ ...patient, isMainPatient: true }, ...dependents];
	}, [patient]);

	const patients = useMemo(() => listAllPatients(), [listAllPatients]);

	const handleSelectPatient = useCallback((selectedPatient) => {
		if (!selectedPatient) {
			return;
		}

		setAnswers({
			patient: selectedPatient
		})

		const path = selectedPatient.attendances?.length > 0 ? `/patient-next-attendances` : 'select-event-group-payment-method';

		NavigationUtils.navigate(history, path);
	}, [history, setAnswers]);

	const handleShowDependentForm = useCallback(() => {
		const patientToUpdate = !patient.cpf && !patient.foreign_passport && !patient.national_immigration_registry;

		if (patientToUpdate) {
			clearAnswers();
			return NavigationUtils.navigate(history, '/patient-update-document');
		}

		setDependentData({ born: '', gender: '' });
		setShowDependentForm(true);
	}, [history, patient, clearAnswers]);

	const handleBackToPatientList = () => {
		setShowDependentForm(false);
	};

	const formatDependentField = (field, value) => {
		if (field === 'born') {
			return formatDate(value);
		}

		return value;
	};

	const onDependentDataChange = (field, value) => {
		const formattedValue = formatDependentField(field, value);

		setDependentData(previousData => ({
			...previousData,
			[field]: formattedValue
		}));
	};

	const handleConfirmNewDependent = useCallback(() => {
		if (!dependentData.born || !dependentData.gender) {
			return;
		}

		clearAnswers();

		setAnswers({
			patient: {
				born: dependentData.born,
				gender: dependentData.gender,
				isNewDependent: true
			}
		});

		setShowDependentForm(false);
		NavigationUtils.navigate(history, '/select-event-group-payment-method');
	}, [history, dependentData, clearAnswers, setAnswers]);

	const isFormValid = dependentData.born?.length === 10 && dependentData.gender;

	return (
		<PatientResponsibleForm
			variant={settings?.theme?.name}
			patients={patients}
			handleCreateNewDependent={handleShowDependentForm}
			handleSelectPatient={handleSelectPatient}
			listMaxHeight={listMaxHeight}
			showDependentForm={showDependentForm}
			dependentData={dependentData}
			onBackToPatientList={handleBackToPatientList}
			onDependentDataChange={onDependentDataChange}
			onConfirmNewDependent={handleConfirmNewDependent}
			isFormValid={isFormValid}
		/>
	);
}

export default PatientResponsibleContainer;
